import { useSortable } from "@dnd-kit/sortable";
import {
  Button,
  Menu,
  MenuPopover,
  PositioningImperativeRef,
  Tooltip,
} from "@fluentui/react-components";
import {
  AddRegular,
  ArrowDown16Filled,
  ArrowUp16Filled,
  ArrowUpRightRegular,
  ArrowUpload16Filled,
  ArrowUploadRegular,
  ChevronDoubleLeftRegular,
  ChevronDownFilled,
  ChevronDownRegular,
  ChevronUpFilled,
  ChevronUpRegular,
  DocumentBulletListRegular,
  DocumentMultipleRegular,
  FolderOpenRegular,
  LockClosedRegular,
  MoreHorizontal24Regular,
  OpenRegular,
  OrganizationRegular,
  PersonRegular,
  PlugConnectedRegular,
  PuzzlePieceRegular,
  ReOrderDotsVerticalFilled,
  ServerRegular,
  ShieldRegular,
  TableFreezeRowRegular,
  WindowRegular,
  WrenchScrewdriverRegular,
} from "@fluentui/react-icons";
import classNames from "classnames";
import React, {
  ReactElement,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import { useAppRouteParams } from "../../../AppRoutes";
import { Sheet } from "../../../domain/Sheet";
import {
  SheetScoreSummaryItem,
  parseScoreValue,
} from "../../../domain/SheetScoreSummary";
import { Version } from "../../../domain/Version";
import { themePrimaryDark } from "../../../themes";

import { AppSideNavNameTooltip } from "../AppSideNavNameTooltip";

import { useSheetScoreSummaryItems } from "./hooks";
import styles from "./index.module.css";
import { parseIntegerFloat } from "./parser";

import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { IconCloudSQL } from "@/components/icons/IconCloudSQL";
import { useAuthorizedSheets } from "@/hooks/useAuthorizedCollections";
import { translation } from "@/i18n";
import { nsSideNav } from "@/i18n/locales/sideNav";
import { nsVersion } from "@/i18n/locales/version";
import { parseDateTimeOrISODateTime } from "@/utils/dateFnsHelper";

const useTranslation = translation.use(nsSideNav, nsVersion);

const nop = () => {};

const menuItemTooltipClassName = classNames(
  styles.tooltipNoPointerEvents,
  styles.tooltipHiddenWithTarget
);

export const TmpMenuSetChevronDownIcon: React.FC = () => (
  <MoreHorizontal24Regular style={{ width: "12px", height: "12px" }} />
);

export const TmpMenuSetDivider: React.FC<{ className?: string }> = ({
  className,
}) => {
  return <div className={classNames(styles.divider, className)} />;
};

const buttonStyle: React.CSSProperties = {
  display: "inline-block",
  width: "22px",
  height: "22px",
  textAlign: "center",
  color: "inherit",
};

const otherIcons: {
  [key: string]: React.FC<React.HTMLAttributes<SVGElement>>;
} = {
  CloudSQL: IconCloudSQL,
  Firestore: () => null,
};

const sideNavLegacyIcons: {
  [key: string]: React.ComponentType<React.SVGProps<SVGSVGElement>>;
} = {
  Add: AddRegular,
  Upload: ArrowUpload16Filled,
  Contact: PersonRegular,
  Org: OrganizationRegular,
  ChromeBack: ChevronDoubleLeftRegular,
  PageList: DocumentBulletListRegular,
  StackIndicator: DocumentMultipleRegular,
  PreviewLink: OpenRegular,
  AnalyticsView: WindowRegular,
  Puzzle: PuzzlePieceRegular,
  PlugConnected: PlugConnectedRegular,
  Shield: ShieldRegular,
  Server: ServerRegular,
  Build: WrenchScrewdriverRegular,
  OpenFolderHorizontal: FolderOpenRegular,
  ChevronDown: ChevronDownRegular,
  ChevronUp: ChevronUpRegular,
  ArrowUpRight: ArrowUpRightRegular,
  TadSheets: TableFreezeRowRegular,
};

const renderSideNavLegacyIcon = ({
  iconName,
  iconColor,
  style,
}: {
  iconName: string;
  iconColor: string;
  style: React.CSSProperties;
}) => {
  const Icon = sideNavLegacyIcons[iconName];
  if (!Icon) return null;
  return (
    <Icon
      style={{
        ...style,
        color: iconColor,
      }}
    />
  );
};

/**
 * @description 各ページなどのリンクを貼るコンポーネント
 * 管理者モードの時は色が反転
 * @param id 主にソート用の識別子
 * @param text リンクタイトル
 * @param active 現在のリンクにいるかどうか。trueだと背景が変わる
 * @param to クリック時のリンク先string
 * @param onClick クリック時の起動関数。to(Link)と併用可能で、その場合はLink遷移と同時に実行される
 * @param contextMenuItems リンク右のボタンを押した時のメニュー一覧. fluent UI
 * @param isChildren 他のTmpMenuSetの子供かどうか。marginが変化
 * @param leftIconName 左側に出るメインのアイコン名
 */
export const SortableTmpMenuSet: React.FC<{
  id: string;
  text?: string;
  to?: string;
  referencedSheetVersionId?: string;
  /** 参照先の表示名。TADS参照など、バージョン名以外を括弧書きするときに使う。 */
  referencedDisplayName?: string;
  active?: boolean;
  onClick?: () => void;
  contextMenuItems?: ReactElement;
  iconName?: string;
  iconColor?: string;
  iconElement?: ReactElement;
  isHidden?: boolean;
  isChildren?: boolean;
  isSortable?: boolean;
  isDisableTooltip?: boolean;
  isAdminMode?: boolean;
  nestedElement?: ReactElement;
  onMoveUp?: (id: string) => void;
  onMoveDown?: (id: string) => void;
}> = ({
  id,
  text = "",
  referencedSheetVersionId,
  referencedDisplayName,
  active = false,
  to,
  onClick,
  contextMenuItems,
  iconName = "",
  iconColor = "var(--figma-color-sideMenuText)",
  iconElement,
  isHidden = false,
  isChildren = false,
  isSortable = false,
  isDisableTooltip = false,
  isAdminMode = false,
  nestedElement,
  onMoveUp,
  onMoveDown,
}) => {
  const { t } = useTranslation();
  const { versions } = useVersions();
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [inHover, setInHover] = useState(false);
  const [inChevronButtonHover, setInChevronButtonHover] = useState(false);
  const [inArrowUpButtonHover, setInArrowUpButtonHover] = useState(false);
  const [inArrowDownButtonHover, setInArrowDownButtonHover] = useState(false);
  const [inMoreButtonHover, setInMoreButtonHover] = useState(false);
  const [inReOrderButtonHover, setInReOrderButtonHover] = useState(false);
  const [isOpenNestedElement, setIsOpenNestedElement] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const buttonRef = React.useRef<HTMLDivElement>(null);
  const positioningRef = useRef<PositioningImperativeRef>(null);

  useEffect(() => {
    if (buttonRef.current) {
      positioningRef.current?.setTarget(buttonRef.current);
    }
  }, [buttonRef, positioningRef]);

  /**
   * dnd-kit
   */
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const dndStyle: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : "",
    transition,
  };

  const highlightStyle = (() => {
    const isSemiActive =
      isOpenNestedElement || inHover || isContextMenuVisible || isFocused;
    const isActive = active;
    return {
      ...(isSemiActive
        ? {
            backgroundColor: "var(--figma-color-sideMenuHover)",
            cursor: "pointer",
          }
        : {}),
      ...(isActive
        ? {
            backgroundColor: "var(--figma-color-sideMenuSelected)",
            cursor: "pointer",
          }
        : {}),
    };
  })();

  // to があれば Link、onClick のみなら button で描画してキーボード操作を担保する。
  // to と onClick は併用可能で、その場合は Link 遷移と同時に onClick も実行される。
  const useLink = !!to;

  const textContent = (
    <div
      className={styles.menuNodeLeft}
      style={{ position: "relative", zIndex: 1 }}
    >
      <div
        className={classNames(styles.text, {
          [styles.isAdminMode]: isAdminMode,
        })}
      >
        <AppSideNavNameTooltip
          content={{
            children: text,
            className: menuItemTooltipClassName,
          }}
          disabled={transform != null || isDisableTooltip}
          withArrow
          relationship="description"
        >
          <span>
            {referencedSheetVersionId || referencedDisplayName ? (
              <>
                {`${text}`}{" "}
                <span className={styles.referencedSheetDisplayName}>
                  {`（${
                    referencedDisplayName ??
                    versions.find((v) => v.id === referencedSheetVersionId)
                      ?.title ??
                    t.version.unknownTitle()
                  }）`}
                </span>
              </>
            ) : (
              text
            )}
          </span>
        </AppSideNavNameTooltip>
      </div>
      {isHidden ? (
        <LockClosedRegular
          style={{
            marginLeft: "5px",
            width: "18px",
            height: "18px",
            flexShrink: 0,
          }}
        />
      ) : null}
    </div>
  );

  const menuNode = (
    <div
      onMouseEnter={() => {
        setInHover(true);
      }}
      onMouseLeave={() => {
        setInHover(false);
      }}
    >
      <div
        className={classNames(styles.menuNode, {
          [styles.children]: isChildren,
        })}
        style={highlightStyle}
        ref={buttonRef}
      >
        {/* アイコン部分 */}
        <div
          style={{
            display: "inline-flex",
            width: "40px",
            height: "40px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {iconElement
            ? iconElement
            : otherIcons[iconName]
              ? React.createElement(otherIcons[iconName], {
                  style: {
                    display: "block",
                    width: "20px",
                    height: "20px",
                    color: iconColor,
                  },
                })
              : renderSideNavLegacyIcon({
                  iconName,
                  iconColor,
                  style: {
                    width: "20px",
                    height: "20px",
                    flexShrink: 0,
                  },
                })}
        </div>
        {/* リンク可能な部分: テキスト */}
        {useLink ? (
          <Link
            to={to}
            onClick={onClick}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={styles.stretchedLink}
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              textDecoration: "none",
              color: "inherit",
              minWidth: 0,
            }}
          >
            {textContent}
          </Link>
        ) : onClick ? (
          <button
            type="button"
            onClick={onClick}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={styles.stretchedLink}
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
              padding: 0,
              border: "none",
              background: "none",
              font: "inherit",
              color: "inherit",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            {textContent}
          </button>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
            }}
          >
            {textContent}
          </div>
        )}
        {/* インタラクティブなボタン群（リンクの外） */}
        {inHover && contextMenuItems ? (
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", zIndex: 1 }}
          >
            {nestedElement ? (
              <Button
                onMouseEnter={() => {
                  setInChevronButtonHover(true);
                }}
                onMouseLeave={() => {
                  setInChevronButtonHover(false);
                }}
                icon={
                  isOpenNestedElement ? (
                    <ChevronUpFilled />
                  ) : (
                    <ChevronDownFilled />
                  )
                }
                style={{
                  color: "inherit",
                  backgroundColor: inChevronButtonHover
                    ? "var(--figma-color-sideMenuHover)"
                    : "transparent",
                }}
                appearance="transparent"
                onClick={(e) => {
                  e.preventDefault();
                  setIsOpenNestedElement((state) => !state);
                }}
              />
            ) : null}
            <Button
              onMouseEnter={() => {
                setInMoreButtonHover(true);
              }}
              onMouseLeave={() => {
                setInMoreButtonHover(false);
              }}
              icon={<MoreHorizontal24Regular />}
              style={{
                color: "inherit",
                backgroundColor: inMoreButtonHover
                  ? "var(--figma-color-sideMenuHover)"
                  : "transparent",
              }}
              appearance="transparent"
              onClick={(e) => {
                e.preventDefault();
                setIsContextMenuVisible((b) => !b);
              }}
            />
            {isSortable ? (
              <Button
                onMouseEnter={() => {
                  setInReOrderButtonHover(true);
                }}
                onMouseLeave={() => {
                  setInReOrderButtonHover(false);
                }}
                icon={<ReOrderDotsVerticalFilled />}
                style={{
                  color: "inherit",
                  backgroundColor: inReOrderButtonHover
                    ? "var(--figma-color-sideMenuHover)"
                    : "transparent",
                }}
                appearance="transparent"
                {...listeners}
                {...attributes}
              />
            ) : null}
          </div>
        ) : null}
        {inHover && (onMoveUp || onMoveDown) ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ position: "relative", zIndex: 1 }}
          >
            {onMoveUp && (
              <Button
                onMouseEnter={() => {
                  setInArrowUpButtonHover(true);
                }}
                onMouseLeave={() => {
                  setInArrowUpButtonHover(false);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp(id);
                }}
                icon={<ArrowUp16Filled />}
                style={{
                  color: "inherit",
                  backgroundColor: inArrowUpButtonHover
                    ? "var(--figma-color-sideMenuHover)"
                    : "transparent",
                }}
                appearance="transparent"
              />
            )}
            {onMoveDown && (
              <Button
                onMouseEnter={() => {
                  setInArrowDownButtonHover(true);
                }}
                onMouseLeave={() => {
                  setInArrowDownButtonHover(false);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown(id);
                }}
                icon={<ArrowDown16Filled />}
                style={{
                  color: "inherit",
                  backgroundColor: inArrowDownButtonHover
                    ? "var(--figma-color-sideMenuHover)"
                    : "transparent",
                }}
                appearance="transparent"
              />
            )}
          </div>
        ) : null}
      </div>
      {nestedElement && isOpenNestedElement ? (
        <div
          className={styles.nestedElement}
          style={{
            ...highlightStyle,
          }}
        >
          {nestedElement}
        </div>
      ) : null}
      {contextMenuItems ? (
        <Menu
          open={isContextMenuVisible}
          positioning={{ positioningRef, position: "after", align: "top" }}
          onOpenChange={() => {
            setIsContextMenuVisible(false);
          }}
        >
          <MenuPopover
            style={{
              width: "max-content",
              maxWidth: "none",
              whiteSpace: "nowrap",
            }}
          >
            {contextMenuItems}
          </MenuPopover>
        </Menu>
      ) : null}
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        ...dndStyle,
        ...(isOpenNestedElement
          ? {
              marginBottom: "3px",
            }
          : {}),
      }}
    >
      {menuNode}
    </div>
  );
};

const diffTypeKinds = [
  { label: "▲", color: "red" },
  { label: "―", color: "#999" },
  { label: "▼", color: "green" },
];

const diffTypeKindsHigherRated = [
  { label: "▼", color: "red" },
  { label: "―", color: "#999" },
  { label: "▲", color: "green" },
];

const ParentScoreItem: React.FC<
  {
    isHaveChildItems?: boolean;
    isOpenChildItems: boolean;
    setIsOpenChildItems: React.Dispatch<React.SetStateAction<boolean>>;
    comparedSheetScoreSummaryItems: SheetScoreSummaryItem[];
  } & SheetScoreSummaryItem
> = memo(
  ({
    name,
    unit,
    value,
    value_type,
    is_higher_rated,
    isHaveChildItems = false,
    isOpenChildItems,
    setIsOpenChildItems,
    comparedSheetScoreSummaryItems,
  }) => {
    // 評価アイコン
    const compareSheetScoreItem = comparedSheetScoreSummaryItems.find(
      ({ name: _name, value_type: _value_type }) =>
        name === _name && value_type === _value_type
    );
    const diffType = useMemo(() => {
      // NOTE: 0以下だと悪い結果
      const diff = (() => {
        if (!compareSheetScoreItem || is_higher_rated === undefined) return 0;
        if (
          (value_type === "float" || value_type === "integer") &&
          (compareSheetScoreItem.value_type === "float" ||
            compareSheetScoreItem.value_type === "integer")
        ) {
          const compareBorderScore =
            parseIntegerFloat(
              compareSheetScoreItem.value,
              compareSheetScoreItem.value_type
            ) ?? 0;
          const score = parseIntegerFloat(value, value_type) ?? 0;

          return !is_higher_rated
            ? compareBorderScore - score
            : score - compareBorderScore;
        }
        return 0;
      })();
      return !is_higher_rated
        ? diffTypeKinds[Math.sign(diff) + 1]
        : diffTypeKindsHigherRated[Math.sign(diff) + 1];
    }, [
      compareSheetScoreItem?.value,
      compareSheetScoreItem?.value_type,
      name,
      unit,
      value,
      value_type,
      is_higher_rated,
    ]);

    const parsedValue = parseScoreValue({ value, value_type });
    return (
      <div className={styles.parentItem}>
        <div className={styles.nameAndIcon}>
          <Tooltip
            content={{
              children: name + ": " + parsedValue + unit,
              className: styles.tooltipHiddenWithTarget,
            }}
            relationship="description"
          >
            <div className={styles.name}> {name}</div>
          </Tooltip>
          {isHaveChildItems ? (
            <Button
              className={styles.icon}
              icon={
                isOpenChildItems ? <ChevronUpFilled /> : <ChevronDownFilled />
              }
              appearance="transparent"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpenChildItems((state) => !state);
              }}
            />
          ) : null}
        </div>
        <div className={styles.score}>
          <div className={styles.valueAndUnit}>
            <div className={styles.value}>{unit ? parsedValue : ""}</div>
            <div className={styles.unit}>{unit ?? ""}</div>
          </div>
          <div className={styles.diff} style={{ color: diffType.color }}>
            {diffType.label}
          </div>
        </div>
      </div>
    );
  }
);

const SummaryScoreNode: React.FC<{
  nestRank: number;
  parentScoreSummaryItem: SheetScoreSummaryItem;
  childSummaryNodeList: React.ReactNode[];
  comparedSheetScoreSummaryItems: SheetScoreSummaryItem[];
}> = ({
  nestRank,
  parentScoreSummaryItem,
  childSummaryNodeList,
  comparedSheetScoreSummaryItems,
}) => {
  const [isOpenChildItems, setIsOpenChildItems] = useState(false);
  const isVisibleChildItems =
    childSummaryNodeList.length > 0 && (isOpenChildItems || nestRank === 0);
  return (
    <div>
      {nestRank === 0 ? null : (
        <ParentScoreItem
          key={parentScoreSummaryItem.name}
          isOpenChildItems={isOpenChildItems}
          setIsOpenChildItems={setIsOpenChildItems}
          isHaveChildItems={childSummaryNodeList.length > 0}
          comparedSheetScoreSummaryItems={comparedSheetScoreSummaryItems}
          {...parentScoreSummaryItem}
        />
      )}
      {isVisibleChildItems ? (
        <div className={nestRank === 0 ? styles.rootItems : styles.childItems}>
          {childSummaryNodeList.map((childItemNode, index) => (
            <div key={`${parentScoreSummaryItem.name}_childNodeList_${index}`}>
              {childItemNode}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const renderRecursiveScoreSummary = ({
  parentScoreSummaryItem,
  restItems,
  comparedSheetScoreSummaryItems,
  nestRank,
}: {
  parentScoreSummaryItem: SheetScoreSummaryItem;
  restItems: SheetScoreSummaryItem[];
  comparedSheetScoreSummaryItems: SheetScoreSummaryItem[];
  nestRank: number;
}): {
  summaryScoreNode: React.ReactNode;
  restItems: SheetScoreSummaryItem[];
  comparedSheetScoreSummaryItems: SheetScoreSummaryItem[];
} => {
  // NOTE: 初回かどうかで処理が分岐
  const childItems = restItems.filter((restItem) => {
    if (nestRank === 0) return restItem.parent === "";
    return restItem.parent === parentScoreSummaryItem.name;
  });
  // 親を除く
  let newRestItems = [...restItems].filter(({ name }) => {
    if (nestRank === 0) return true;
    return name !== parentScoreSummaryItem.name;
  });

  // 再起処理実行
  // NOTE: childItemsの長さが0なら何もしない
  const childSummaryNodeList: React.ReactNode[] = [];

  // nestRankはひとまず視認性のため3までとする
  // todo いずれ無限にネストを許容する
  if (nestRank < 3) {
    childItems.forEach((newParentScoreItem) => {
      // 子供に対して実行
      const { summaryScoreNode, restItems: gotRestItems } =
        renderRecursiveScoreSummary({
          parentScoreSummaryItem: newParentScoreItem,
          restItems: newRestItems,
          comparedSheetScoreSummaryItems,
          nestRank: nestRank + 1,
        });
      // restItemsを更新
      newRestItems = [...gotRestItems];
      childSummaryNodeList.push(summaryScoreNode);
    });
  }

  return {
    summaryScoreNode: (
      <SummaryScoreNode
        nestRank={nestRank}
        parentScoreSummaryItem={parentScoreSummaryItem}
        childSummaryNodeList={childSummaryNodeList}
        comparedSheetScoreSummaryItems={comparedSheetScoreSummaryItems}
      />
    ),
    restItems: newRestItems,
    comparedSheetScoreSummaryItems,
  };
};

export const TmpMenuSetOptimizeSummary: React.FC<{
  version: Version;
  compareTargetResultScoreSummary?: Sheet | null;
}> = ({ version, compareTargetResultScoreSummary = null }) => {
  const { t } = useTranslation();
  const { versionId, organizationId } = useAppRouteParams();
  const [sheets, isLoadedSheets] = useAuthorizedSheets(version.id);

  // 自身のスコアサマリを探索
  const resultScoreSummary: Sheet | null = useMemo(
    () =>
      sheets.find(
        ({ sheetOptimizeType }) =>
          sheetOptimizeType === "result_score_summaries"
      ) ?? null,
    [sheets]
  );
  // 自身のスコアサマリ
  const sheetScoreSummaryItems = useSheetScoreSummaryItems({
    resultScoreSummary,
    versionId: version.id,
    organizationId,
  });
  // 比較対象のスコアサマリ
  const comparedSheetScoreSummaryItems = useSheetScoreSummaryItems({
    resultScoreSummary: compareTargetResultScoreSummary,
    versionId,
    organizationId,
  });
  // 一番上の親のサマリを出す
  const parentScoreSummaryItem = useMemo(
    () =>
      sheetScoreSummaryItems.find(({ parent }) => parent === "") ?? {
        ...sheetScoreSummaryItems[0],
      },
    [sheetScoreSummaryItems]
  );

  // ここでスコアオープンに関するstateを持って、下位に分配する
  // オープンしている親リストみたいな
  const { summaryScoreNode, restItems } = renderRecursiveScoreSummary({
    parentScoreSummaryItem,
    restItems: sheetScoreSummaryItems,
    comparedSheetScoreSummaryItems,
    nestRank: 0,
  });

  const parsedDate = parseDateTimeOrISODateTime(version.createdAt);

  if (!isLoadedSheets) {
    return null;
  }
  return (
    <div className={styles.optimizeSummaryContainer}>
      <Tooltip
        content={{
          children: `ID: ${version.id}`,
          className: styles.tooltipHiddenWithTarget,
        }}
        relationship="description"
      >
        <div className={styles.top}>
          <div className={styles.versionId}>ID: {version.id}</div>
        </div>
      </Tooltip>
      {sheetScoreSummaryItems.length === 0 ? (
        // NOTE: createdAtのパースに成功し、かつ作成(複製)時間から2分以内なら更新中とみなす
        parsedDate && parsedDate.getTime() + 120 * 1000 > Date.now() ? (
          <div>{t.sideNav.versionSummary.preparing()}</div>
        ) : (
          <div className={styles.noSummary}>
            {t.sideNav.versionSummary.empty()}
          </div>
        )
      ) : (
        <>
          {summaryScoreNode}
          {restItems.map((item) => {
            return (
              <ParentScoreItem
                key={item.name}
                isOpenChildItems={false}
                setIsOpenChildItems={nop}
                comparedSheetScoreSummaryItems={comparedSheetScoreSummaryItems}
                {...item}
              />
            );
          })}
        </>
      )}
    </div>
  );
};
